import React, { useState } from "react";
import PublicLayout from "../components/PublicLayout";
import { Mail, MessageCircle, Headphones, Twitter, Instagram, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const channels = [
  {
    icon: Mail,
    title: "Email support",
    body: "Fastest way to reach us. We answer every email within 24 hours.",
    href: "mailto:contact@syllox.in",
    cta: "contact@syllox.in",
  },
  {
    icon: Twitter,
    title: "X (Twitter)",
    body: "Quick questions, updates, and the occasional NEET meme.",
    href: "https://x.com/Sylloxin",
    cta: "@Sylloxin",
  },
  {
    icon: Instagram,
    title: "Instagram",
    body: "Behind-the-scenes, study tips, and aspirant spotlights.",
    href: "https://www.instagram.com/syllox.in?igsh=Y2tpaml5MDZmZHpw",
    cta: "@syllox.in",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setSending(true);
    const subject = encodeURIComponent(`[Syllox Contact] ${form.subject || "Hello"}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}\n\n—\nSent from syllox.in/contact`
    );
    window.location.href = `mailto:contact@syllox.in?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSending(false);
      toast.success("Opened your email app. We'll reply within 24 hours.");
    }, 600);
  };

  return (
    <PublicLayout
      title="Contact Us · Syllox"
      description="Get in touch with the Syllox team. Email contact@syllox.in or send us a message — we reply within 24 hours."
    >
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="contact-hero">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Contact</div>
        <h1 className="mt-4 font-heading text-4xl font-bold leading-tight sm:text-5xl">We'd love to hear from you</h1>
        <p className="mt-4 max-w-2xl text-zinc-600 dark:text-zinc-400 sm:text-lg">
          Got a question, a bug report, a feature idea, or just want to say hi? We read everything and reply fast.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {channels.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.title}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="group rounded-2xl border border-zinc-200 bg-white p-6 transition hover:border-emerald-500 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
              >
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 transition group-hover:bg-emerald-500 group-hover:text-white">
                  <Icon size={20}/>
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold">{c.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{c.body}</p>
                <div className="mt-3 text-sm font-medium text-emerald-600">{c.cta} →</div>
              </a>
            );
          })}
        </div>
      </section>

      <section id="support" className="mx-auto max-w-3xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500 text-white">
              <MessageCircle size={20}/>
            </div>
            <h2 className="font-heading text-2xl font-semibold">Send us a message</h2>
          </div>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            This opens your default email app with a pre-filled draft to contact@syllox.in.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">Your name</Label>
                <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name"/>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email"/>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject <span className="text-xs text-zinc-400">(optional)</span></Label>
              <Input id="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Bug report, feature request, billing…" data-testid="contact-subject"/>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message"/>
            </div>
            <Button type="submit" disabled={sending} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="contact-submit">
              {sending ? "Opening email…" : "Send message"}
            </Button>
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-50 p-5 dark:bg-emerald-500/10">
          <div className="flex items-start gap-3">
            <Headphones size={20} className="mt-0.5 shrink-0 text-emerald-600"/>
            <div>
              <div className="font-semibold text-zinc-900 dark:text-zinc-100">Premium support</div>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                Premium subscribers get priority email support with replies within 6 hours on business days. Mention your registered phone or email so we can pull up your account fast.
              </p>
              <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 size={14}/> Average response time: <b>under 6 hours</b>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
